import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./SharedSpendLanding.module.css";
import {
  HeroSection,
  FeaturesSection,
  HowItWorksSection,
  FAQSection,
  CTASection,
} from "../components/sections/SharedSpend";
import { navigationLinks } from "../data/sharedSpendData";

const LogoIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={styles.logoIcon}>
    <path d="M12 6v12m-3-2.818l.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
  </svg>
);

export const SharedSpendLanding: React.FC = () => {
  const [isScrolled, setIsScrolled] = React.useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMobileMenuOpen(false);
  };

  return (
    <div className={styles.page}>
      <a href="#main-content" className={styles.skipLink}>
        Skip to main content
      </a>

      <nav className={`${styles.nav} ${isScrolled ? styles.navScrolled : ""}`}>
        <div className={styles.navContainer}>
          <Link to="/shared-spend" className={styles.logo}>
            <LogoIcon />
            <span>Shared Spend</span>
          </Link>

          <ul className={styles.navLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <button
                  onClick={() => scrollToSection(link.id)}
                  className={styles.navLink}
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>

          <Link to="/shared-spend/waitlist" className={styles.navCta}>
            Join Waitlist
          </Link>

          <button
            className={styles.mobileMenuButton}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
          >
            <span className={`${styles.hamburger} ${isMobileMenuOpen ? styles.hamburgerOpen : ""}`} />
          </button>
        </div>

        <div className={`${styles.mobileMenu} ${isMobileMenuOpen ? styles.mobileMenuOpen : ""}`}>
          <ul className={styles.mobileNavLinks}>
            {navigationLinks.map((link) => (
              <li key={link.id}>
                <button
                  onClick={() => scrollToSection(link.id)}
                  className={styles.mobileNavLink}
                >
                  {link.label}
                </button>
              </li>
            ))}
            <li>
              <Link
                to="/shared-spend/waitlist"
                className={styles.mobileCta}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                Join the Waitlist
              </Link>
            </li>
          </ul>
        </div>
      </nav>

      <main id="main-content" role="main" tabIndex={-1}>
        <HeroSection />
        <FeaturesSection />
        <HowItWorksSection />
        <FAQSection />
        <CTASection />
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerContainer}>
          <div className={styles.footerBrand}>
            <Link to="/shared-spend" className={styles.footerLogo}>
              <LogoIcon />
              <span>Shared Spend</span>
            </Link>
            <p className={styles.footerTagline}>Budget together, stress less</p>
          </div>

          <div className={styles.footerLinks}>
            <div className={styles.footerColumn}>
              <h3>Product</h3>
              <ul>
                <li><button onClick={() => scrollToSection("features")}>Features</button></li>
                <li><button onClick={() => scrollToSection("how-it-works")}>How It Works</button></li>
                <li><button onClick={() => scrollToSection("faq")}>FAQ</button></li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h3>Legal</h3>
              <ul>
                <li><Link to="/shared-spend/privacy">Privacy Policy</Link></li>
              </ul>
            </div>
            <div className={styles.footerColumn}>
              <h3>Company</h3>
              <ul>
                <li><Link to="/">Dixon App Development</Link></li>
                <li><a href="mailto:dixonappdevelopment@gmail.com">Contact</a></li>
              </ul>
            </div>
          </div>
        </div>

        <div className={styles.footerBottom}>
          <p>&copy; {new Date().getFullYear()} Dixon App Development. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};
